import React, { type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import type {
	ActiveTriggerState,
	SetTriggerStateParams,
	TriggerContextDispatchProps,
	TriggerContextProps,
	UseFirePostOfficeTrigger,
	UseTrigger,
} from './types';

// Preserved legacy state shape — only used when post-office_enable-trigger-record-state is off.
// Do not modify: this type and its associated logic below are intentionally kept verbatim from
// the original implementation so reviewers can verify the fallback path is unchanged.
type LegacyActiveTriggerState = {
	activeTriggerId: string | undefined;
};

const initialActiveTriggerState: TriggerContextProps = {
	activeTriggerState: {},
};

const initialTriggerDispatchState: TriggerContextDispatchProps = {
	setTriggerState: () => {
		// eslint-disable-next-line no-console
		console.error(
			'No Post Office Trigger Context found. Did you remember to add a Post Office Provider?',
		);
	},
};

const TriggerContext = React.createContext<TriggerContextProps>(initialActiveTriggerState);

const TriggerDispatchContext = React.createContext<TriggerContextDispatchProps>(
	initialTriggerDispatchState,
);

export const TriggerContextProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	// Legacy state — type and update logic are verbatim from the original implementation
	const [legacyState, setLegacyState] = useState<LegacyActiveTriggerState>({
		activeTriggerId: undefined,
	});

	// New record state — each trigger tracks its own boolean independently
	const [recordState, setRecordState] = useState<ActiveTriggerState>({});

	const setTriggerState = useCallback<(params: SetTriggerStateParams) => void>(
		({ isActive, triggerId }) => {
			if (fg('post-office_enable-trigger-record-state')) {
				// New: each trigger tracks its own state independently.
				// Mutual exclusion is enforced at render time by the choreographer.
				setRecordState((current) => {
					if (!!current[triggerId] === isActive) {
						return current;
					}
					if (isActive) {
						return { ...current, [triggerId]: true };
					}
					const { [triggerId]: excludedTrigger, ...rest } = current;
					return rest;
				});
			} else {
				// Legacy: verbatim original logic — only one trigger active at a time
				setLegacyState((current) => {
					const hasStateChanged =
						current.activeTriggerId !== triggerId ||
						(current.activeTriggerId === triggerId && isActive === false);
					if (!hasStateChanged) {
						return current;
					}
					if (isActive) {
						return { activeTriggerId: triggerId };
					}
					return { activeTriggerId: undefined };
				});
			}
		},
		[],
	);

	// Unified record view exposed to context consumers.
	// In legacy mode, converts the single activeTriggerId to a one-entry record
	// so useTrigger works identically for both paths.
	const activeTriggerState = useMemo<ActiveTriggerState>(() => {
		if (fg('post-office_enable-trigger-record-state')) {
			return recordState;
		}
		const { activeTriggerId } = legacyState;
		return activeTriggerId ? { [activeTriggerId]: true } : {};
	}, [recordState, legacyState]);

	const triggerContextValue = useMemo(() => ({ activeTriggerState }), [activeTriggerState]);

	const triggerContexDispatchValue = useMemo(() => ({ setTriggerState }), [setTriggerState]);

	return (
		<TriggerContext.Provider value={triggerContextValue}>
			<TriggerDispatchContext.Provider value={triggerContexDispatchValue}>
				{children}
			</TriggerDispatchContext.Provider>
		</TriggerContext.Provider>
	);
};

// Public Hooks

// For use in dev tools
export const useTriggerContext = (): TriggerContextProps => useContext(TriggerContext);
export const useTriggerDispatchContext = (): TriggerContextDispatchProps =>
	useContext(TriggerDispatchContext);

// For use in messages
export const useTrigger: UseTrigger = (triggerId) => {
	const { activeTriggerState } = useTriggerContext();

	return triggerId ? { isActive: !!activeTriggerState[triggerId] } : { isActive: undefined };
};

// For use externally
export const useFirePostOfficeTrigger: UseFirePostOfficeTrigger = ({ triggerId }) => {
	const { setTriggerState: setContextTriggerState } = useTriggerDispatchContext();

	const fireTrigger = useCallback(
		({ isActive }: { isActive: boolean }) => {
			return setContextTriggerState({ triggerId, isActive });
		},
		[setContextTriggerState, triggerId],
	);

	return {
		fireTrigger,
	};
};

// For use in EP external messages, to suppor the EP Deprecation/migration
// this works with the ep-migration-external-messages message template
export const usePostOfficeExernalMessageProxy = (
	messageId: string,
): {
	hasMessageStarted: boolean | undefined;
	requestMessageStart: () => void;
	dismissMessage: () => void;
	handlePostOfficeClickTracking: () => void;
} => {
	const { activeTriggerState } = useTriggerContext();

	// In legacy mode (gate off), the record has at most one true entry, so we reconstruct
	// activeTriggerId to preserve the original guard logic verbatim.
	// In record mode (gate on), the guard is unnecessary — setTriggerState is a no-op if
	const activeTriggerId = fg('post-office_enable-trigger-record-state')
		? undefined
		: Object.keys(activeTriggerState).find((k) => activeTriggerState[k]);

	const hasMessageAlreadyStarted = activeTriggerId?.endsWith('-started');

	// listeners
	const { isActive: hasMessageStarted } = useTrigger(`${messageId}-started`);

	// actions
	const { fireTrigger: requestMessageStartTrigger } = useFirePostOfficeTrigger({
		triggerId: `${messageId}-start`,
	});
	const { fireTrigger: fireDismissClickTrigger } = useFirePostOfficeTrigger({
		triggerId: `${messageId}-dismiss`,
	});
	const { fireTrigger: fireCtaClickTrigger } = useFirePostOfficeTrigger({
		triggerId: `${messageId}-cta-click`,
	});

	const requestMessageStart = useCallback((): void => {
		if (hasMessageAlreadyStarted) {
			return;
		}
		requestMessageStartTrigger({ isActive: true });
	}, [requestMessageStartTrigger, hasMessageAlreadyStarted]);

	const dismissMessage = useCallback((): void => {
		fireDismissClickTrigger({ isActive: true });
	}, [fireDismissClickTrigger]);

	const handlePostOfficeClickTracking = useCallback((): void => {
		fireCtaClickTrigger({ isActive: true });
	}, [fireCtaClickTrigger]);

	return {
		hasMessageStarted,
		requestMessageStart,
		dismissMessage,
		handlePostOfficeClickTracking,
	};
};
